import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";

import { languageAtom } from "~/Atom";

import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import { receptionBowEndExpression } from "~/reception/motion/receptionBowMotion";
import { useInactivityTimeout } from "~/utils/receptionUtils";
import { resolveErrorTypeFromCode } from "~/utils/error";

import { useErrorMessageResolver } from "~/components/hooks/useErrorMessageResolver";
import { ROUTE } from "~/constants/routes";

import "~/css/reception/ErrorPage.css";
import ReceptionHeader from "~/components/ReceptionHeader";
import ReceptionCharacterCanvas from "~/components/reception/ReceptionCharacterCanvas";
import FitText from "~/components/reception/FitText";

/**
 * エラーコードに応じた案内文を表示し、必要に応じて音声案内も行うエラー画面。
 */
const ErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();

  const uiCode = params.get("code");

  const [language] = useAtom(languageAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
    // 発話終了後にお辞儀モーションを再生する（受付の全画面で統一）。
    endExpression: receptionBowEndExpression(),
  });

  useInactivityTimeout(navigate);

  /**
   * エラーコードがない状態で直接アクセスされた場合は、受付開始画面へ戻す。
   */
  useEffect(() => {
    if (!uiCode) {
      navigate(ROUTE.RECEPTION.LOADING, { state: { skipStartupModal: true } });
    }
  }, [uiCode, navigate]);

  /**
   * エラーコードから表示対象のメッセージ種別を判定する。
   */
  const resolverKey = resolveErrorTypeFromCode(uiCode ?? "");

  const resolvedMessage = useErrorMessageResolver(resolverKey, language, t);
  const title = resolvedMessage?.title ?? "";
  const content = resolvedMessage?.content ?? "";

  /**
   * タイトル、本文を読み上げ用テキストに結合する。
   */
  const voiceText = [title, content].filter(Boolean).join("。");

  /**
   * 利用可能な音声エンジンと言語条件を満たす場合に、エラー内容を読み上げる。
   */
  useEffect(() => {
    if (resolvedMessage === null) return; // wait until resolver finished
    void playWithCooldown(voiceText).catch((error) => {
      console.error("errorPage playVoice failed:", error);
    });
  }, [playWithCooldown, voiceText]);

  const handleTop = () => {
    navigate(ROUTE.RECEPTION.MENU, { replace: true });
  };

  return (
    <div className="reception-error-page">
      <ReceptionHeader />
      <div className="reception-error__content">
        {title && (
          <h2 className="screen__title">
            <FitText>{title}</FitText>
          </h2>
        )}
        <div className="reception-error__sub-content">
          {uiCode && (
            <div className="reception-error__code">
              <FitText>{`${t("error")}：[${uiCode}]`}</FitText>
            </div>
          )}
          {content && (
            <p className="reception-error__guidance">
              <FitText>{content}</FitText>
            </p>
          )}
        </div>
        <div className="reception-error__character-panel">
          <ReceptionCharacterCanvas canvasStyle={{ height: "100%", aspectRatio: "1 / 1" }} />
        </div>
        <button type="button" className="text-btn gradient-primary-btn" onClick={handleTop}>
          <span>{t("completeReception.backToTop")}</span>
        </button>
      </div>
    </div>
  );
};

export default ErrorPage;
